// Aperçus des tanks dans les menus, dessinés avec le même code que le jeu (art.ts, variants.ts).
// - tankImage : image fixe mise en cache, rendue par un seul moteur WebGL partagé (cartes, arbre, évolutions).
// - TankStage : scène animée (hangar, aperçu de boutique) : la tourelle suit le pointeur, un clic tire.
// Le nombre de contextes WebGL reste bas : un moteur partagé pour les images, une scène par grand aperçu.
import { Application, Container, Graphics, Rectangle } from 'pixi.js';
import type { ClassNode } from '../../../shared/catalog/classes';
import { COSMETICS } from '../../../shared/shop';
import { ACCENT_SELF, ART_R, drawAutoTurrets, drawHull, drawHullFamily, drawTurret, type Palette } from '../game/render/art';
import { drawFamilyDetail, drawRankPips, drawTankParts } from '../game/render/variants';
import { Particles } from '../game/render/particles';

/** Apparence : code de skin du jeu (0–5 styles de base, 10+ cosmétiques) ou palette libre (plans de récompenses). */
export type TankLook = { code: number } | { palette: Palette };

/** Code de skin du jeu pour un cosmétique (mêmes règles que le serveur) : 0 = variantes par classe. */
export function skinCodeOf(id: string | null | undefined): number {
  if (!id || id.startsWith('base:')) return 0;
  const i = COSMETICS.findIndex((c) => c.id === id);
  return i >= 0 ? 10 + i : 0;
}

/** Plan technique : silhouette au trait, pour les récompenses dont le visuel n'est pas encore une palette du jeu. */
export const BLUEPRINT: Palette = { hull: 0x1c2631, plate: 0x223040, tread: 0x161e27, barrel: 0x1c2631, band: 0x2a3a4c, outline: 0x82b4dc };

interface TankParts {
  root: Container;
  hull: Graphics;
  turret: Graphics;
  auto: Graphics;
  hasAuto: boolean;
}

function buildTank(cls: ClassNode, look: TankLook, accent: number): TankParts {
  const root = new Container();
  const parts = { hull: new Graphics(), turret: new Graphics(), auto: new Graphics() };
  let hasAuto: boolean;
  if ('code' in look) hasAuto = drawTankParts(parts, cls, look.code, accent);
  else {
    const p = look.palette;
    drawHull(parts.hull, p);
    drawHullFamily(parts.hull, cls, p);
    drawFamilyDetail(parts.hull, cls, p);
    drawRankPips(parts.hull, cls, p);
    drawTurret(parts.turret, cls, p, accent);
    hasAuto = drawAutoTurrets(parts.auto, cls, p, accent);
  }
  root.addChild(parts.hull, parts.turret, parts.auto);
  return { root, ...parts, hasAuto };
}

/** Plus grande distance au centre de la caisse (tubes compris), pour cadrer quelle que soit l'orientation. */
function extent(t: TankParts): number {
  let m = ART_R;
  for (const g of [t.hull, t.turret, t.auto]) {
    const b = g.getLocalBounds();
    m = Math.max(m, Math.hypot(Math.max(Math.abs(b.minX), Math.abs(b.maxX)), Math.max(Math.abs(b.minY), Math.abs(b.maxY))));
  }
  return m;
}

const lookKey = (look: TankLook) => ('code' in look ? `c${look.code}` : `p${Object.values(look.palette).join('.')}`);

// ── Images fixes ─────────────────────────────────────────
let shared: Promise<Application> | null = null;
function sharedApp(): Promise<Application> {
  shared ??= (async () => {
    const app = new Application();
    await app.init({ width: 8, height: 8, backgroundAlpha: 0, antialias: true, autoStart: false, preference: 'webgl' });
    app.ticker.stop();
    return app;
  })();
  return shared;
}

const cache = new Map<string, string>();
const pending = new Map<string, Promise<string>>();
let queue: Promise<unknown> = Promise.resolve();

/** Image PNG (URL de données) d'un tank vu de dessus, nez vers le haut, tourelle orientée selon `angle`. */
export function tankImage(cls: ClassNode, look: TankLook, px: number, opts: { angle?: number; accent?: number } = {}): Promise<string> {
  const angle = opts.angle ?? -Math.PI / 4;
  const accent = opts.accent ?? ACCENT_SELF;
  const key = `${cls.id}|${lookKey(look)}|${px}|${angle.toFixed(2)}|${accent}`;
  const hit = cache.get(key);
  if (hit) return Promise.resolve(hit);
  const wait = pending.get(key);
  if (wait) return wait;
  const job = queue.then(async () => {
    const app = await sharedApp();
    const res = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    const wrap = new Container();
    const t = buildTank(cls, look, accent);
    t.hull.rotation = -Math.PI / 2;
    t.turret.rotation = angle;
    t.auto.rotation = angle + Math.PI / 3;
    // L'image est fixe : on cadre la silhouette réelle (tubes orientés compris), centrée dans le carré.
    const b = t.root.getLocalBounds();
    const bw = b.maxX - b.minX;
    const bh = b.maxY - b.minY;
    const s = (px - 6) / (Math.max(bw, bh) + 6);
    const shadow = new Graphics(t.hull.context);
    shadow.rotation = -Math.PI / 2;
    shadow.tint = 0x000000;
    shadow.alpha = 0.32;
    shadow.position.set(2.5, 4);
    const body = new Container();
    body.addChild(shadow, t.root);
    body.scale.set(s);
    body.position.set(px / 2 - (b.minX + bw / 2) * s, px / 2 - (b.minY + bh / 2) * s);
    wrap.addChild(body);
    const canvas = app.renderer.extract.canvas({ target: wrap, frame: new Rectangle(0, 0, px, px), resolution: res, clearColor: [0, 0, 0, 0] }) as HTMLCanvasElement;
    const url = canvas.toDataURL('image/png');
    wrap.destroy({ children: true });
    cache.set(key, url);
    pending.delete(key);
    return url;
  });
  const p = job as Promise<string>;
  queue = p.catch(() => undefined);
  pending.set(key, p);
  return p;
}

// ── Scène animée ─────────────────────────────────────────
interface Shot {
  g: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

export interface StageOptions {
  /** Taille du tank en pixels CSS (rayon de la caisse). */
  radius: number;
  accent?: number;
  /** Animation coupée : une seule image, aucun mouvement. */
  still?: boolean;
  onFire?: () => void;
}

export class TankStage {
  private app: Application;
  private world = new Container();
  private body = new Container();
  private shotLayer = new Container();
  private fxLayer = new Container();
  private particles: Particles;
  private tank: TankParts | null = null;
  private shots: Shot[] = [];
  private aim = -Math.PI / 4;
  private target = -Math.PI / 4;
  private idle = 0;
  private recoil = 0;
  private cls: ClassNode | null = null;
  private scaleK = 1;
  private destroyed = false;
  private ro: ResizeObserver | null = null;

  private constructor(
    private readonly host: HTMLElement,
    app: Application,
    private readonly opts: StageOptions,
  ) {
    this.app = app;
    this.particles = new Particles(this.fxLayer);
    this.world.addChild(this.shotLayer, this.body, this.fxLayer);
    app.stage.addChild(this.world);
    this.layout();
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(host);
    if (opts.still) app.ticker.stop();
    else app.ticker.add((tk) => this.step(Math.min(0.05, tk.deltaMS / 1000)));
  }

  static async create(host: HTMLElement, opts: StageOptions): Promise<TankStage> {
    const app = new Application();
    await app.init({
      resizeTo: host,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(2, window.devicePixelRatio || 1),
      preference: 'webgl',
    });
    app.canvas.classList.add('tank-stage-canvas');
    host.appendChild(app.canvas);
    return new TankStage(host, app, opts);
  }

  private layout(): void {
    const w = this.host.clientWidth || 1;
    const h = this.host.clientHeight || 1;
    this.world.position.set(w / 2, h / 2);
    this.render();
  }

  setTank(cls: ClassNode, look: TankLook): void {
    if (this.destroyed) return;
    this.body.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.cls = cls;
    const t = buildTank(cls, look, this.opts.accent ?? ACCENT_SELF);
    t.hull.rotation = -Math.PI / 2;
    const shadow = new Graphics(t.hull.context);
    shadow.rotation = -Math.PI / 2;
    shadow.tint = 0x000000;
    shadow.alpha = 0.35;
    shadow.position.set(3, 5);
    this.body.addChild(shadow, t.root);
    // Rayon de caisse demandé, mais le tank entier (tubes compris) doit tenir dans le cadre.
    const w = this.host.clientWidth || 200;
    const h = this.host.clientHeight || 200;
    const fit = (Math.min(w, h) / 2 - 8) / (extent(t) + 4);
    this.scaleK = Math.min(this.opts.radius / ART_R, fit);
    this.body.scale.set(this.scaleK);
    this.tank = t;
    this.applyAim();
    this.render();
  }

  /** Vise un point de l'écran (coordonnées client). */
  aimAt(clientX: number, clientY: number): void {
    const r = this.app.canvas.getBoundingClientRect();
    const dx = clientX - (r.left + r.width / 2);
    const dy = clientY - (r.top + r.height / 2);
    if (Math.hypot(dx, dy) < 6) return;
    this.target = Math.atan2(dy, dx);
    this.idle = 0;
    if (this.opts.still) {
      this.aim = this.target;
      this.applyAim();
      this.render();
    }
  }

  fire(): void {
    if (!this.tank || !this.cls || this.destroyed) return;
    this.opts.onFire?.();
    if (this.opts.still) return;
    this.recoil = 1;
    const k = this.scaleK;
    const accent = this.opts.accent ?? ACCENT_SELF;
    for (const b of this.cls.weapon.barrels) {
      if (b.auto) continue;
      const a = this.aim + (b.angle * Math.PI) / 180;
      const ox = -Math.sin(a) * b.offset * ART_R * k;
      const oy = Math.cos(a) * b.offset * ART_R * k;
      const L = b.length * ART_R * k;
      const x = Math.cos(a) * L + ox;
      const y = Math.sin(a) * L + oy;
      const r = Math.max(3, b.width * ART_R * k * 0.42);
      const g = new Graphics().circle(0, 0, r).fill(accent).stroke({ width: Math.max(1.5, r * 0.25), color: 0x1a3b48 });
      g.position.set(x, y);
      this.shotLayer.addChild(g);
      const v = 620;
      this.shots.push({ g, x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 1.4 });
      this.particles.flash(x, y, 0xffd28a, r * 1.4, 0.1);
      this.particles.burst(x, y, 0xffb04a, 5, 180, Math.max(1.5, r * 0.35), 0, 0.25);
      if (this.shots.length > 24) this.shots.shift()?.g.destroy();
    }
  }

  private applyAim(): void {
    if (!this.tank) return;
    const kick = this.recoil * 0.12 * ART_R;
    this.tank.turret.rotation = this.aim;
    this.tank.turret.position.set(-Math.cos(this.aim) * kick, -Math.sin(this.aim) * kick);
    this.tank.auto.rotation = this.aim * 0.6 + performance.now() / 2400;
  }

  private step(dt: number): void {
    if (this.destroyed) return;
    this.idle += dt;
    // Sans pointeur depuis 3 s : balayage lent, comme un tank qui guette.
    if (this.idle > 3) this.target = -Math.PI / 2 + Math.sin(performance.now() / 1900) * 0.9;
    let d = this.target - this.aim;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    this.aim += d * Math.min(1, dt * 9);
    this.recoil = Math.max(0, this.recoil - dt * 6);
    this.applyAim();
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    for (let i = this.shots.length - 1; i >= 0; i--) {
      const s = this.shots[i];
      s.life -= dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.g.position.set(s.x, s.y);
      s.g.alpha = Math.min(1, s.life * 3);
      if (s.life <= 0 || Math.abs(s.x) > w || Math.abs(s.y) > h) {
        s.g.destroy();
        this.shots.splice(i, 1);
      }
    }
    this.particles.update(dt);
  }

  private render(): void {
    if (!this.destroyed && this.opts.still) this.app.render();
  }

  /** Hors de l'écran ou onglet caché : l'animation s'arrête (batterie des téléphones). */
  setActive(active: boolean): void {
    if (this.destroyed || this.opts.still) return;
    if (active) this.app.ticker.start();
    else this.app.ticker.stop();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.ro?.disconnect();
    // Jamais destroy(true) : cela viderait les réserves globales de Pixi (lots de rendu) encore utilisées
    // par les autres applications ouvertes, qui planteraient au rendu suivant.
    this.app.destroy({ removeView: true }, { children: true });
  }
}
